import React, { createContext, useContext, useState, useEffect } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { apiFetch } from "../utils/api";
import AccountChooser from "../components/AccountChooser";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msal, setMsal] = useState(true);
  const [appUser, setAppUser] = useState(null);
  const [currentPartner, setCurrentPartner] = useState(null);
  const [pendingOpportunityItem, setPendingOpportunityItem] = useState();
  const [currentOpportunityId, setCurrentOpportunityId] = useState();  
  const [viewDialogEditData, setViewDialogEditData] = useState(null);
  const [isViewOpportunityOpen, setIsViewOpportunityOpen] = useState(false);
  const [editOpportunityData, setEditOpportunityData] = useState(null);
  const [viewOpportunity, setViewOpportunity] = useState(null);
  const [isViewDialogInEditMode, setIsViewDialogInEditMode] = useState(false);
  const [accountChoices, setAccountChoices] = useState([]);

  useEffect(() => {
    const initMsal = async () => {
      const res = await fetch("/auth");
      const cfg = await res.json();

      const msalInstance = new PublicClientApplication({
        auth: {
          clientId: cfg.clientId,
          authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
          redirectUri: `${window.location.origin}/dashboard`,
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: false,
        },
      });
      await msalInstance.initialize();
      setMsal(msalInstance);

      // Handle redirect after returning from Azure login
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        msalInstance.setActiveAccount(response.account);
        setUser(response.account);
      } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 1) {
          msalInstance.setActiveAccount(accounts[0]);
          setUser(accounts[0]);
        } else if (accounts.length > 1) {
          setAccountChoices(accounts);
        }
      }
      setLoading(false);
    };
    initMsal();
  }, []);

  useEffect(() => {
    const fetchAppUser = async () => {
      const res = apiFetch(await idToken(), "/api/users/current");
      res.then(async (current_user_response) => {
        setAppUser(current_user_response.data);
        setCurrentPartner(current_user_response.data.partners[0])
      })
    };
    if (!loading && !appUser) {
      fetchAppUser();
    }
  }, [user]);

  const login = () => {
    if (!msal) return;
    msal.loginRedirect({
      scopes: ["openid", "profile", "email"],
    });
  };

  const logout = () => {
    if (!msal) return;
    const account = msal.getActiveAccount();
    msal.logoutRedirect({
      account,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const token = async () => {
    if (!msal) return null;
    const account = msal.getActiveAccount();
    if (!account) return null;

    const response = await msal.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account,
    });

    return response.accessToken;
  };

  const idToken = async () => {
    if (!msal) return null;
    const account = msal.getActiveAccount();
    if (!account) return null;

    try {
      const response = await msal.acquireTokenSilent({
        scopes: ["openid", "profile", "email"],
        account,
      });

      return response.idToken;
    } catch (error) {
      console.error("Token acquisition failed:", error);
      // If silent token acquisition fails, trigger interactive login
      if (error.name === 'InteractionRequiredAuthError') {
        await msal.acquireTokenRedirect({
          scopes: ["openid", "profile", "email"],
          account,
        });
      }
      throw error;
    }
  };

  const canAccessModule = (module) => {
    if (module == 'Dashboard') {
      return true;
    }
    if (!loading && appUser) {
      let roles = appUser.roles.map(it => it.name);
      if (roles.includes('Admin')) {
        return true;
      }
      let permissions = appUser.roles.flatMap(it => it.permissions).filter(it => it.level != 'deny').map(it => it.name);
      if (permissions.includes(module)) {
        return true;
      }
      console.log(`Access denied :${module}`)
      return false;
    }
    return false;
  };

  const handleAccountSelect = (account) => {
    msal.setActiveAccount(account);
    setUser(account);
    setAccountChoices([]);
  };


  return (
    <AuthContext.Provider value={
      {
        user,
        token,
        idToken,
        login,
        logout,
        loading,
        canAccessModule,
        appUser,
        currentPartner,
        setCurrentPartner,
        pendingOpportunityItem,
        setPendingOpportunityItem,
        currentOpportunityId,
        setCurrentOpportunityId,
        viewDialogEditData,
        setViewDialogEditData,
        isViewOpportunityOpen,
        setIsViewOpportunityOpen,
        editOpportunityData,
        setEditOpportunityData,
        setViewOpportunity,
        viewOpportunity,
        isViewDialogInEditMode,
        setIsViewDialogInEditMode
      }}>
      {accountChoices.length > 0 ? (
        <AccountChooser accounts={accountChoices} onSelect={handleAccountSelect} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);